/**
 * @file Экран содержимого одного хранилища.
 *
 * Собирает готовые фичи: папку, квоту, создание элементов и передачи.
 * Обработчики уходят в hooks и Provider передач; здесь остаются разметка и навигация.
 */
import { FiArrowLeft, FiChevronRight, FiFolder } from 'react-icons/fi'
import { Navigate, useParams } from 'react-router-dom'

import FileRow from '../components/FileRow'
import { useCustomer } from '../context/useCustomer'
import { downloadMessages } from '../features/downloads/messages'
import { fileMessages } from '../features/files/messages'
import { folderBreadcrumbs, sortFolderItems } from '../features/files/model'
import NewFolder from '../features/folders/NewFolder'
import TrafficBalance from '../features/traffic/TrafficBalance'
import { useTrafficQuota } from '../features/traffic/useTrafficQuota'
import PasswordPrompt from '../features/unlock/PasswordPrompt'
import UploadControl from '../features/uploads/UploadControl'
import { useUploadRefresh } from '../features/uploads/useUploadRefresh'
import { useFileTransfer } from '../hooks/useFileTransfer'
import { useFolder } from '../hooks/useFolder'
import { useTunnelConnection } from '../hooks/useTunnelConnection'
import { useLocale } from '../i18n/useLocale'
import './Box.css'

const Box = () => {
    // Пользователь, маршрут и язык — исходные данные для остальных hooks.
    const { customer, password, setPassword } = useCustomer()
    const { boxId } = useParams()
    const { locale } = useLocale()
    const text = fileMessages[locale]

    // Подготовка квоты и подключение; чтение баланса и списка ждёт connected.
    const {
        phase,
        connecting,
        connected,
        error: connectionError,
        retry: reconnect,
    } = useTunnelConnection({
        boxId,
        token: customer!.api_token,
    })
    const connectionMessage = phase ? text.connectionPhases[phase] : ''

    const quota = useTrafficQuota({ boxId, connected })

    const folderHook = useFolder({
        boxID: boxId!,
        token: customer?.api_token,
        connected,
    })
    useUploadRefresh(
        boxId,
        folderHook.currentFolder,
        folderHook.refresh,
        quota.refresh,
    )

    // Скачивание запускается из общего Provider, а hook связывает его со строками папки.
    const transfer = useFileTransfer({
        refreshQuota: quota.refresh,
        currentFolder: folderHook.currentFolder,
        password: password || '',
        boxID: boxId!,
        token: customer?.api_token,
    })
    if (!boxId || !customer) return null

    if (!customer.boxes.some((storage) => storage.id === boxId))
        return <Navigate to='/' replace />

    if (connectionError)
        return (
            <div className='file-browser-error' role='alert'>
                <p>{text.connectionError}</p>

                <button type='button' onClick={reconnect}>
                    {text.retryConnection}
                </button>
            </div>
        )

    if (connecting && password === null)
        return (
            <p className='file-list-status' role='status'>
                {connectionMessage}
            </p>
        )

    if (password === null)
        return (
            <PasswordPrompt
                key={`${boxId}:${folderHook.currentFolder}`}
                boxID={boxId}
                folderPath={folderHook.currentFolder}
                token={customer.api_token}
                onSubmit={setPassword}
            />
        )

    // Производные данные не требуют отдельного state: считаем их из полученного списка.
    const items = sortFolderItems(folderHook.folderContent?.items ?? [], locale)
    const names = new Set(items.map((item) => item.name))

    const crumbs = folderBreadcrumbs(folderHook.currentFolder)
    const disabled = !connected || folderHook.loading

    return (
        <section className='file-browser' aria-labelledby='file-browser-title'>
            {/* Общая шапка хранилища и баланс существуют независимо от загрузки списка папки. */}
            <header className='file-browser-header'>
                <div>
                    <h1 id='file-browser-title'>{text.title}</h1>

                    <p>
                        {text.storage}{' '}
                        <span className='file-storage-id' title={boxId}>
                            {boxId}
                        </span>
                    </p>
                </div>

                <TrafficBalance quota={quota} connected={connected} />
            </header>

            {/* Переход по цепочке меняет текущую папку; useFolder отменяет устаревшее чтение. */}
            <nav className='file-breadcrumbs' aria-label={text.location}>
                {!folderHook.isAtRoot && (
                    <button
                        type='button'
                        className='file-icon-button file-parent'
                        title={text.parent}
                        aria-label={text.parent}
                        onClick={() =>
                            folderHook.setCurrentFolder(
                                crumbs[crumbs.length - 2].path,
                            )
                        }
                    >
                        <FiArrowLeft aria-hidden='true' />
                    </button>
                )}

                <ol>
                    {crumbs.map((crumb, index) => (
                        <li key={crumb.path}>
                            {index > 0 && <FiChevronRight aria-hidden='true' />}
                            {index === crumbs.length - 1 ? (
                                <span aria-current='page' title={crumb.name}>
                                    {index === 0 ? text.root : crumb.name}
                                </span>
                            ) : (
                                <button
                                    type='button'
                                    title={crumb.name}
                                    onClick={() =>
                                        folderHook.setCurrentFolder(crumb.path)
                                    }
                                >
                                    {index === 0 ? text.root : crumb.name}
                                </button>
                            )}
                        </li>
                    ))}
                </ol>
            </nav>

            {transfer.notice && (
                <p className='file-browser-error' role='alert'>
                    {transfer.notice}
                </p>
            )}

            <div className='file-list' tabIndex={-1}>
                {/* Панель создания общая для нового файла и папки; формы выполняют разные операции. */}
                <NewFolder
                    key={`${boxId}:${folderHook.currentFolder}`}
                    currentFolder={folderHook.currentFolder}
                    existingNames={names}
                    disabled={disabled}
                    onCreate={folderHook.createFolder}
                    onRefresh={folderHook.refresh}
                    refreshFailed={false}
                    toolbarActions={
                        <UploadControl
                            boxId={boxId}
                            folder={folderHook.currentFolder}
                            token={customer.api_token}
                            password={password}
                            disabled={disabled}
                        />
                    }
                />

                {/* Не скрываем уже полученные файлы при ошибке обновления; показываем сообщение рядом. */}
                {folderHook.error && (
                    <div className='file-browser-error' role='alert'>
                        <p>
                            {folderHook.folderContent
                                ? text.refreshError
                                : text.loadError}
                        </p>

                        <button
                            type='button'
                            onClick={folderHook.refresh}
                            disabled={folderHook.loading}
                        >
                            {text.refresh}
                        </button>
                    </div>
                )}
                {(connecting || folderHook.loading) && (
                    <p className='file-list-status' role='status'>
                        {connecting ? connectionMessage : text.loading}
                    </p>
                )}
                {!connecting &&
                    !folderHook.loading &&
                    !folderHook.error &&
                    !items.length && (
                        <div className='file-list-empty'>
                            <FiFolder aria-hidden='true' />

                            <h2>{text.empty}</h2>

                            <p>{text.emptyHint}</p>
                        </div>
                    )}

                {/* Каждая строка получает действия через props и не обращается к HTTP напрямую. */}
                {items.map((item) => {
                    const path = `${folderHook.currentFolder}/${item.name}`
                    const archiving = transfer.archivingFolders.includes(path)
                    const downloading = transfer.downloadingFiles.includes(path)
                    const mutating = folderHook.isMutating(item.name)

                    return (
                        <FileRow
                            key={`${boxId}:${path}`}
                            name={item.name}
                            isFolder={item.kind === 'folder'}
                            existingNames={names}
                            disabled={!connected || folderHook.loading}
                            taskInProgress={
                                mutating || archiving || downloading
                            }
                            activity={
                                mutating
                                    ? text.working
                                    : archiving
                                      ? downloadMessages[locale].phases
                                            .preparing
                                      : downloading
                                        ? downloadMessages[locale].phases
                                              .downloading
                                        : null
                            }
                            onOpen={() => folderHook.setCurrentFolder(path)}
                            onDownload={() =>
                                item.kind === 'folder'
                                    ? transfer.downloadFolder(item.name)
                                    : transfer.downloadFile(item.name)
                            }
                            onDelete={() =>
                                item.kind === 'folder'
                                    ? folderHook.deleteFolder(item.name)
                                    : folderHook.deleteFile(item.name)
                            }
                            onRename={(name) =>
                                folderHook.rename(item.name, name)
                            }
                        />
                    )
                })}
            </div>

            {items.length > 0 && (
                <p className='file-list-count'>
                    {text.count}:{' '}
                    {new Intl.NumberFormat(locale).format(items.length)}
                </p>
            )}
        </section>
    )
}

export default Box
